// @ts-nocheck
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CalendarPlus, Loader2, RefreshCw } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { getDivisionColor } from '@/utils/divisionColors';
import { PublicEventHeader } from '@/components/public/PublicEventHeader';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";
import { usePublicLive, useDivisionParam, formatUpdated } from '@/hooks/usePublicLive';
import { formatGameNumber } from '@/lib/gameNumber';
import { formatTime } from '@/lib/dates';

const FN = 'rle-public-schedule';

const NAVY = '#0a1f44';

function timeBlock(start?: string) {
  if (!start) return 'Other';
  // Normalize "9:00 AM" / "09:00" to a clean hour-block label.
  const m = start.trim().match(/^(\d{1,2}):(\d{2})\s*([AaPp][Mm])?/);
  if (!m) return start;
  const hour = parseInt(m[1], 10);
  return formatTime(`${hour}:00`);
}

function groupByTimeBlock(games: any[]) {
  const groups: { label: string; games: any[] }[] = [];
  const index: Record<string, number> = {};
  for (const g of games) {
    const label = timeBlock(g.start);
    if (index[label] === undefined) {
      index[label] = groups.length;
      groups.push({ label, games: [] });
    }
    groups[index[label]].games.push(g);
  }
  return groups;
}

function downloadICS(filename: string, content: string) {
  const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function formatDay(dateStr: string) {
  try {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString(undefined, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

function shortDay(dateStr: string) {
  try {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString(undefined, {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

// Parse "9:00 AM" / "13:00" to minutes since midnight for correct sorting.
function parseTimeMinutes(start?: string): number {
  if (!start) return Number.MAX_SAFE_INTEGER;
  const m = start.trim().match(/^(\d{1,2}):(\d{2})\s*([AaPp][Mm])?/);
  if (!m) return Number.MAX_SAFE_INTEGER;
  let hour = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  const ampm = m[3]?.toUpperCase();
  if (ampm === 'PM' && hour !== 12) hour += 12;
  if (ampm === 'AM' && hour === 12) hour = 0;
  return hour * 60 + min;
}

const STATUS_LABEL: Record<string, string> = {
  scheduled: 'Scheduled',
  in_progress: 'In progress',
  completed: 'Completed',
};

function StatusBadge({ status }: { status?: string }) {
  if (!status) return null;
  const cls =
    status === 'completed'
      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      : status === 'in_progress'
        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
        : 'bg-muted text-muted-foreground';
  return (
    <span className={cn('shrink-0 rounded-full px-2 py-0.5 text-xs font-medium', cls)}>
      {STATUS_LABEL[status] ?? status}
    </span>
  );
}

/** Every team name on a public page is a link to that team's own page. */
const TeamLink: React.FC<{ slug?: string; id?: string | null; name?: string }> = ({ slug, id, name }) =>
  id ? (
    <Link className="underline underline-offset-2 hover:text-foreground" to={`/schedule/${slug}/team/${id}`}>
      {name}
    </Link>
  ) : (
    <>{name}</>
  );

const PublicSchedulePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  // ?preview=draft — an operator's look at the unpublished working draft.
  // Read-only; publishes nothing.
  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get('preview') === 'draft';
  // Division is the primary filter and lives in the URL, so the view is shareable.
  const [division, setDivision] = useDivisionParam();
  const [team, setTeam] = useState('all');
  const [court, setCourt] = useState('all');

  const { data, loading, errorCode, lastUpdated, refreshing, refresh } = usePublicLive(
    FN,
    isPreview ? { action: 'get', slug, preview: true } : { action: 'get', slug },
    { enabled: !!slug },
  );

  const [downloading, setDownloading] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'grid' | 'team'>('list');

  const teamName = useMemo(() => {
    if (team === 'all') return '';
    return data?.teams?.find((t: any) => t.id === team)?.name || '';
  }, [team, data]);

  // Teams follow the division filter: when a division is chosen, only its
  // teams appear in the Team select.
  const divisionTeams = useMemo(() => {
    const teams: any[] = data?.teams || [];
    if (division === 'all') return teams;
    return teams.filter((t) => t.division === division);
  }, [data, division]);

  // If the picked team drops out of the narrowed list, fall back to all teams.
  useEffect(() => {
    if (!data || team === 'all') return;
    if (!divisionTeams.some((t: any) => t.id === team)) setTeam('all');
  }, [data, division, team, divisionTeams]);

  // Distinct courts across all games (first-appearance order) — drives the court filter.
  const courtOptions = useMemo(() => {
    const seen: string[] = [];
    for (const day of data?.days ?? []) {
      for (const g of day.games || []) {
        if (g.court && !seen.includes(g.court)) seen.push(g.court);
      }
    }
    return seen;
  }, [data]);

  const filteredDays = useMemo(() => {
    if (!data?.days) return [];
    return data.days
      .map((day: any) => {
        const games = (day.games || [])
          .filter((g: any) => division === 'all' || g.division === division)
          .filter(
            (g: any) =>
              team === 'all' || g.team_a_id === team || g.team_b_id === team || g.team_a_registration_id === team || g.team_b_registration_id === team,
          )
          .filter((g: any) => court === 'all' || g.court === court)
          .sort((a: any, b: any) => (a.start || '').localeCompare(b.start || ''));
        return { ...day, games };
      })
      .filter((day: any) => day.games.length > 0);
  }, [data, division, team, court]);


  // All days from the response (unfiltered) — drives the day tabs + counts.
  const allDays = useMemo(() => (data?.days ?? []) as any[], [data]);

  // Default the selected day to the first day once data loads.
  useEffect(() => {
    if (allDays.length === 0) {
      setSelectedDate(null);
    } else if (!allDays.some((d) => d.date === selectedDate)) {
      setSelectedDate(allDays[0].date);
    }
  }, [allDays, selectedDate]);

  // Games for the active day, filtered by division + team, sorted by time then court.
  const dayGames = useMemo(() => {
    const day = allDays.find((d) => d.date === selectedDate);
    if (!day) return [] as any[];
    return (day.games || [])
      .filter((g: any) => division === 'all' || g.division === division)
      .filter(
        (g: any) =>
          team === 'all' || g.team_a_id === team || g.team_b_id === team || g.team_a_registration_id === team || g.team_b_registration_id === team,
      )
      .filter((g: any) => court === 'all' || g.court === court)
      .sort((a: any, b: any) => {
        const t = parseTimeMinutes(a.start) - parseTimeMinutes(b.start);
        if (t !== 0) return t;
        return (a.court || '').localeCompare(b.court || '');
      });
  }, [allDays, selectedDate, division, team, court]);


  // List view: group the day's games by division.
  const gamesByDivision = useMemo(() => {
    const groups: { division: string; games: any[] }[] = [];
    const index: Record<string, number> = {};
    for (const g of dayGames) {
      const key = g.division || 'Other';
      if (index[key] === undefined) {
        index[key] = groups.length;
        groups.push({ division: key, games: [] });
      }
      groups[index[key]].games.push(g);
    }
    return groups;
  }, [dayGames]);

  // Grid view: distinct courts (first-appearance order) × distinct start times (ascending).
  const grid = useMemo(() => {
    const courts: string[] = [];
    for (const g of dayGames) {
      const court = g.court || '—';
      if (!courts.includes(court)) courts.push(court);
    }
    const times = [...new Set(dayGames.map((g: any) => g.start).filter(Boolean))].sort(
      (a, b) => parseTimeMinutes(a as string) - parseTimeMinutes(b as string),
    ) as string[];
    const cell = (court: string, time: string) =>
      dayGames.find((g: any) => (g.court || '—') === court && g.start === time);
    return { courts, times, cell };
  }, [dayGames]);

  // By Team view: teams (from the team list) that have games this day, with their games in time order.
  const teamsWithGames = useMemo(() => {
    const teams = (data?.teams ?? []) as { id: string; name: string }[];
    return teams
      .map((t) => ({
        ...t,
        games: dayGames.filter(
          (g: any) => g.team_a_id === t.id || g.team_b_id === t.id,
        ),
      }))
      .filter((t) => t.games.length > 0);
  }, [data, dayGames]);

  const handleICS = async (extra: Record<string, any> = {}) => {
    setDownloading(true);
    try {
      const { data: res, error } = await invokeEdgeFunction(FN, {
        body: { action: 'ics', slug, ...extra },
      });
      if (error || !res?.ok) return;
      const payload = res.data || res;
      if (payload?.content && payload?.filename) {
        downloadICS(payload.filename, payload.content);
      }
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (errorCode === 'NOT_PUBLISHED') {
    return (
      <div className="mx-auto max-w-2xl px-4 py-24 text-center">
        <h1 className="text-2xl font-bold text-foreground">
          Schedule Coming Soon
        </h1>
        <p className="mt-2 text-muted-foreground">This event's schedule hasn't been published yet.</p>
      </div>
    );
  }

  if (errorCode === 'PREVIEW_FORBIDDEN') {
    return (
      <div className="mx-auto max-w-2xl px-4 py-24 text-center">
        <h1 className="text-2xl font-bold text-foreground">
          Only this event's operators can preview its unpublished schedule.
        </h1>
      </div>
    );
  }

  if (errorCode === 'NOT_FOUND') {
    return (
      <div className="mx-auto max-w-2xl px-4 py-24 text-center">
        <h1 className="text-2xl font-bold text-foreground">Event not found.</h1>
      </div>
    );
  }

  if (errorCode || !data) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-24 text-center">
        <h1 className="text-2xl font-bold text-foreground">
          Could not load schedule.
        </h1>
      </div>
    );
  }

  const event = data.event || {};
  // Division colour: whatever the operator set, else the shared palette's
  // automatic pick. The division name always appears next to the colour.
  const divColor = (d?: string | null) => getDivisionColor(d, data?.division_colors?.[d as string] ?? null);

  return (
    <div className="mx-auto max-w-4xl px-4 py-8">
      {isPreview && (
        <div
          role="status"
          data-testid="schedule-preview-banner"
          className="mb-4 rounded-md border-2 border-dashed border-destructive bg-destructive/10 px-4 py-3 text-sm font-semibold text-destructive"
        >
          Preview of an unpublished draft. Coaches and parents cannot see this yet — nothing has been published.
        </div>
      )}
      {/* Header */}
      <PublicEventHeader
        slug={slug ?? ''} current="schedule" division={division}
        event={event} branding={data.branding} tabs={data.tabs || []} teams={data.teams || []}
      />

      {/* Filters + calendar */}
      <div className="mb-8 flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-muted-foreground">
            Division
          </label>
          <Select value={division} onValueChange={setDivision}>
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All divisions</SelectItem>
              {(data.divisions || []).map((d: string) => {
                const c = divColor(d);
                return (
                  <SelectItem key={d} value={d}>
                    <span className="flex items-center gap-2">
                      <span
                        className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                        style={{ backgroundColor: c.dot }}
                      />
                      {d}
                    </span>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-muted-foreground">
            Team
          </label>
          <Select value={team} onValueChange={setTeam}>
            <SelectTrigger className="w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All teams</SelectItem>
              {divisionTeams.map((t: any) => (
                <SelectItem key={t.id} value={t.id}>
                  {division === 'all' && t.division ? `${t.name} · ${t.division}` : t.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {courtOptions.length > 0 && (
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-muted-foreground">
              Court
            </label>
            <Select value={court} onValueChange={setCourt}>
              <SelectTrigger className="w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All courts</SelectItem>
                {courtOptions.map((c: string) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}


        <Button variant="ghost" size="sm" onClick={refresh} disabled={refreshing}>
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
        <span className="pb-2 text-xs text-muted-foreground">
          Updated {formatUpdated(lastUpdated)}
        </span>

        <Button
          variant="outline"
          onClick={() => handleICS()}
          disabled={downloading}
        >
          {downloading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <CalendarPlus className="h-4 w-4" />
          )}
          Add to calendar
        </Button>

        {team !== 'all' && (
          <Button
            variant="outline"
            onClick={() => handleICS({ team_id: team })}
            disabled={downloading}
          >
            <CalendarPlus className="h-4 w-4" />
            Add {teamName} to calendar
          </Button>
        )}
      </div>

      {/* Color legend */}
      {(data.divisions || []).length > 0 && (
        <div className="mb-8 flex flex-wrap items-center gap-2">
          {(data.divisions || []).map((d: string) => {
            const c = divColor(d);
            return (
              <span
                key={d}
                className="flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium"
                style={{ backgroundColor: c.bg, color: c.text }}
              >
                <span
                  className="inline-block h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: c.dot }}
                />
                {d}
              </span>
            );
          })}
        </div>
      )}


      {/* Day tabs with game counts */}
      {allDays.length > 0 && (
        <div className="mb-6 flex flex-wrap gap-2 border-b pb-3">
          {allDays.map((day: any) => {
            const isActive = day.date === selectedDate;
            return (
              <button
                key={day.date}
                onClick={() => setSelectedDate(day.date)}
                className={cn(
                  'flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/70',
                )}
              >
                {shortDay(day.date)}
                <Badge
                  variant="secondary"
                  className={cn(
                    'h-5 min-w-[1.25rem] justify-center px-1.5 text-xs',
                    isActive && 'bg-primary-foreground/20 text-primary-foreground',
                  )}
                >
                  {(day.games || []).length}
                </Badge>
              </button>
            );
          })}
        </div>
      )}

      {/* View mode toggle */}
      <div className="mb-6 inline-flex rounded-md border p-1">
        {([
          { key: 'list', label: 'List' },
          { key: 'grid', label: 'Grid' },
          { key: 'team', label: 'By Team' },
        ] as const).map((v) => (
          <button
            key={v.key}
            onClick={() => setViewMode(v.key)}
            className={cn(
              'rounded px-3 py-1.5 text-sm font-medium transition-colors',
              viewMode === v.key
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {v.label}
          </button>
        ))}
      </div>

      {/* Active day views */}
      {!selectedDate ? (
        <p className="text-muted-foreground">No games to display.</p>
      ) : dayGames.length === 0 ? (
        <p className="text-muted-foreground">No games match these filters.</p>
      ) : viewMode === 'list' ? (
        <section className="mb-10">
          {gamesByDivision.map((group) => {
            const gc = divColor(group.division);
            return (
              <div key={group.division} className="mb-6">
                <h3
                  className="mb-2 inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-semibold"
                  style={{ backgroundColor: gc.bg, color: gc.text }}
                >
                  <span
                    className="inline-block h-2.5 w-2.5 rounded-full"
                    style={{ backgroundColor: gc.dot }}
                  />
                  {group.division}
                </h3>
                <div className="flex flex-col gap-2">
                  {group.games.map((g: any) => {
                    const c = divColor(g.division);
                    return (
                      <div
                        key={g.id}
                        className="group flex flex-col gap-x-4 gap-y-1 rounded-lg border bg-card px-4 py-3 shadow-sm transition-all duration-150 hover:-translate-y-0.5 hover:shadow-md sm:flex-row sm:flex-wrap sm:items-center"
                        style={{ borderLeft: `4px solid ${c.border}` }}
                      >
                        <span className="shrink-0 text-xs font-semibold text-muted-foreground sm:w-24">
                          {formatGameNumber(g) || ''}
                        </span>
                        <span className="shrink-0 text-base font-bold text-foreground sm:w-32">
                          {formatTime(g.start)}
                          {g.end ? `–${formatTime(g.end)}` : ''}
                        </span>
                        <span className="shrink-0 text-sm text-muted-foreground sm:w-24">
                          {g.court}
                        </span>
                        <span className="flex-1 text-sm text-foreground">
                          <TeamLink slug={slug} id={g.team_a_id} name={g.team_a} /> vs{' '}
                          <TeamLink slug={slug} id={g.team_b_id} name={g.team_b} />
                        </span>
{g.game_type === 'crossover' && (
                          <span className="shrink-0 text-[10px] font-semibold text-red-600" title="Crossover game — counts toward both teams' records">
                            Crossover
                          </span>
                        )}
                        <StatusBadge status={g.status} />
                        {g.status === 'completed' &&
                          g.score_a != null &&
                          g.score_b != null && (
                            <span className="text-sm font-semibold text-foreground">
                              {g.score_a}–{g.score_b}
                            </span>
                          )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </section>
      ) : viewMode === 'grid' ? (
        <section className="mb-10 overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr>
                <th className="w-20 p-2 text-left font-medium text-muted-foreground">
                  Time
                </th>
                {grid.courts.map((court) => (
                  <th
                    key={court}
                    className="p-2 text-left font-medium text-muted-foreground"
                  >
                    {court}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grid.times.map((time) => (
                <tr key={time} className="border-t">
                  <td className="p-2 align-top text-muted-foreground">{formatTime(time)}</td>
                  {grid.courts.map((court) => {
                    const g = grid.cell(court, time);
                    if (!g)
                      return <td key={court} className="p-1 align-top" />;
                    const c = divColor(g.division);
                    return (
                      <td key={court} className="p-1 align-top min-w-[160px]">
                        <div
                          className="rounded-md border bg-card p-2"
                          style={{ borderLeft: `4px solid ${c.border}` }}
                        >
                          {formatGameNumber(g) && (
                            <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                              {formatGameNumber(g)}
                            </div>
                          )}
                          <div className="font-medium leading-tight">
                            <TeamLink slug={slug} id={g.team_a_id} name={g.team_a} /> vs{' '}
                            <TeamLink slug={slug} id={g.team_b_id} name={g.team_b} />
                          </div>
                          <div className="mt-1 flex flex-wrap items-center gap-1">
                            {g.division && (
                              <span
                                className="rounded-full px-1.5 py-0.5 text-[10px] font-medium"
                                style={{ backgroundColor: c.bg, color: c.text }}
                              >
                                {g.division}
                              </span>
                            )}
                            {g.game_type === 'crossover' && (
                              <span className="text-[10px] font-semibold text-red-600" title="Crossover game — counts toward both teams' records">
                                Crossover
                              </span>
                            )}
                            <StatusBadge status={g.status} />
                          </div>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : (
        <section className="mb-10 flex flex-col gap-6">
          {teamsWithGames.length === 0 ? (
            <p className="text-muted-foreground">No teams have games this day.</p>
          ) : (
            teamsWithGames.map((t) => (
              <div key={t.id}>
                <h3 className="mb-2 text-base font-semibold" style={{ color: NAVY }}>
                  <Link className="underline underline-offset-2" to={`/schedule/${slug}/team/${t.id}`}>
                    {t.name}
                  </Link>
                </h3>
                <div className="flex flex-col gap-2">
                  {t.games.map((g: any) => {
                    const c = divColor(g.division);
                    const isA = g.team_a_id === t.id;
                    const opponent = isA ? g.team_b : g.team_a;
                    const opponentId = isA ? g.team_b_id : g.team_a_id;
                    return (
                      <div
                        key={g.id}
                        className="flex flex-col gap-x-4 gap-y-1 rounded-lg border bg-card px-4 py-3 shadow-sm sm:flex-row sm:flex-wrap sm:items-center"
                        style={{ borderLeft: `4px solid ${c.border}` }}
                      >
                        <span className="shrink-0 text-base font-bold text-foreground sm:w-32">
                          {formatTime(g.start)}
                          {g.end ? `–${formatTime(g.end)}` : ''}
                        </span>
                        <span className="shrink-0 text-sm text-muted-foreground sm:w-24">
                          {g.court}
                        </span>
                        <span className="flex-1 text-sm text-foreground">
                          vs <TeamLink slug={slug} id={opponentId} name={opponent} />
                        </span>
                        {g.game_type === 'crossover' && (
                          <span className="shrink-0 text-[10px] font-semibold text-red-600" title="Crossover game — counts toward both teams' records">
                            Crossover
                          </span>
                        )}
                        <StatusBadge status={g.status} />
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </section>
      )}
    </div>
  );
};


export default PublicSchedulePage;

